import { useRef, useEffect, useState, useCallback } from 'react';

export default function useCanScroll() {
	const scrollableElRef = useRef<HTMLDivElement>(null);
	const [canScroll, setCanScroll] = useState<{ up: boolean; down: boolean }>({
		up: false,
		down: false,
	});

	const updateCanScroll = useCallback(() => {
		const el = scrollableElRef.current;
		if (!el) return;

		const maxScrollTop = el.scrollHeight - el.clientHeight;
		// We need to increase the tolerance when comparing heights against scrollTop
		// because it can be a fractional value due to subpixel rendering.
		const epsilon = 1;

		setCanScroll({
			up: el.scrollTop > epsilon,
			down: maxScrollTop - el.scrollTop > epsilon,
		});
	}, []);

	useEffect(() => {
		const el = scrollableElRef.current;
		if (!el) return;

		el.addEventListener('scroll', updateCanScroll);

		const observer = new ResizeObserver(updateCanScroll);
		observer.observe(el);

		return () => {
			el.removeEventListener('scroll', updateCanScroll);
			observer?.disconnect();
		};
	}, [updateCanScroll]);

	// recalculate() is an escape hatch to allow consumers to force a recalculation for
	// instances where the content may change but the size of scrollableElRef does not.
	return { canScroll, scrollableElRef, recalculate: updateCanScroll };
}
